import React, { useState, useEffect, useCallback } from 'react';
import API_URL from './config';

// Convert 24-hour format to 12-hour format
function formatTime12h(time24) {
  if (!time24) return '';
  
  // Handle range format like "15:30-16:30" (no spaces)
  if (time24.includes('-') && !time24.includes(' ')) {
    const [start, end] = time24.split('-');
    return `${formatSingleTime(start)} - ${formatSingleTime(end)}`;
  }
  
  // Handle range format like "15:30 - 16:30" (with spaces)
  if (time24.includes(' - ')) {
    const [start, end] = time24.split(' - ');
    return `${formatSingleTime(start)} - ${formatSingleTime(end)}`;
  }
  
  return formatSingleTime(time24);
}

function formatSingleTime(time24) {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return time24;
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function MyInvoices({ token }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [timezone, setTimezone] = useState('America/New_York');

  // Fetch timezone setting
  useEffect(() => {
    fetch(`${API_URL}/api/settings/timezone`)
      .then(r => r.json())
      .then(data => { if (data.value) setTimezone(data.value); })
      .catch(() => {});
  }, []);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/my-invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInvoices(data);
      }
    } catch (err) {
      console.error('Error fetching invoices:', err);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const fetchInvoiceDetail = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedInvoice(data);
      }
    } catch (err) {
      console.error('Error fetching invoice:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const datePart = dateStr.slice(0, 10);
    const [year, month, day] = datePart.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', { timeZone: timezone });
  };

  const formatMoney = (amount) => {
    return '$' + Number(amount || 0).toFixed(2);
  };

  const getStatusBadge = (status) => {
    const colors = {
      sent: { bg: '#dbeafe', color: '#1d4ed8' },
      viewed: { bg: '#e0e7ff', color: '#4338ca' },
      partial: { bg: '#fef3c7', color: '#92400e' },
      paid: { bg: '#dcfce7', color: '#166534' },
      overdue: { bg: '#fee2e2', color: '#dc2626' },
      cancelled: { bg: '#f3f4f6', color: '#6b7280' }
    };
    const style = colors[status] || colors.sent;
    return (
      <span style={{
        background: style.bg,
        color: style.color,
        padding: '0.25rem 0.75rem',
        borderRadius: '12px',
        fontWeight: '500',
        fontSize: '0.85rem',
        textTransform: 'capitalize'
      }}>
        {status}
      </span>
    );
  };

  if (loading) {
    return <div className="my-invoices"><p>Loading invoices...</p></div>;
  }

  // Invoice Detail View
  if (selectedInvoice) {
    return (
      <div className="my-invoices invoice-detail">
        <div className="invoices-header">
          <button className="back-btn" onClick={() => setSelectedInvoice(null)}>← Back to Invoices</button>
        </div>

        <div className="invoice-header-info">
          <div>
            <h3>{selectedInvoice.invoice_number}</h3>
            <p>Issued: {formatDate(selectedInvoice.issued_date)}</p>
            {selectedInvoice.due_date && <p>Due: {formatDate(selectedInvoice.due_date)}</p>}
          </div>
        </div>

        <h4>Line Items</h4>
        <table className="invoice-items-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Date</th>
              <th>Time</th>
              <th className="text-right">Price</th>
            </tr>
          </thead>
          <tbody>
            {selectedInvoice.items && selectedInvoice.items.map(item => (
              <tr key={item.id}>
                <td>{item.description}</td>
                <td>{formatDate(item.booking_date)}</td>
                <td>{formatTime12h(item.time_slot)}</td>
                <td className="text-right">{formatMoney(item.line_total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="3" className="text-right"><strong>Subtotal:</strong></td>
              <td className="text-right">{formatMoney(selectedInvoice.subtotal)}</td>
            </tr>
            {Number(selectedInvoice.tax_amount) > 0 && (
              <tr>
                <td colSpan="3" className="text-right">Tax ({selectedInvoice.tax_rate}%):</td>
                <td className="text-right">{formatMoney(selectedInvoice.tax_amount)}</td>
              </tr>
            )}
            <tr className="total-row">
              <td colSpan="3" className="text-right"><strong>Total:</strong></td>
              <td className="text-right"><strong>{formatMoney(selectedInvoice.total)}</strong></td>
            </tr>
            {Number(selectedInvoice.amount_paid) > 0 && (
              <tr>
                <td colSpan="3" className="text-right">Amount Paid:</td>
                <td className="text-right">{formatMoney(selectedInvoice.amount_paid)}</td>
              </tr>
            )}
            {Number(selectedInvoice.balance_due) > 0 && (
              <tr className="balance-row">
                <td colSpan="3" className="text-right"><strong>Balance Due:</strong></td>
                <td className="text-right"><strong>{formatMoney(selectedInvoice.balance_due)}</strong></td>
              </tr>
            )}
          </tfoot>
        </table>

        {selectedInvoice.notes && (
          <div className="invoice-notes">
            <h4>Notes</h4>
            <p>{selectedInvoice.notes}</p>
          </div>
        )}

        {selectedInvoice.payments && selectedInvoice.payments.length > 0 && (
          <div className="payments-section">
            <h4>Payment History</h4>
            <table className="payments-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Reference</th>
                </tr>
              </thead>
              <tbody>
                {selectedInvoice.payments.map(p => (
                  <tr key={p.id}>
                    <td>{formatDate(p.payment_date)}</td>
                    <td>{formatMoney(p.amount)}</td>
                    <td style={{ textTransform: 'capitalize' }}>{p.payment_method?.replace('_', ' ') || '-'}</td>
                    <td>{p.reference_number || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // Invoice List View
  return (
    <div className="my-invoices">
      <h2>My Invoices</h2>
      
      {invoices.length === 0 ? (
        <p className="no-invoices">No invoices found.</p>
      ) : (
        <table className="invoices-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Date</th>
              <th>Due Date</th>
              <th>Total</th>
              <th>Balance Due</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map(inv => (
              <tr key={inv.id}>
                <td>{inv.invoice_number}</td>
                <td>{formatDate(inv.issued_date)}</td>
                <td>{formatDate(inv.due_date)}</td>
                <td>{formatMoney(inv.total)}</td>
                <td className={Number(inv.balance_due) > 0 ? 'balance-due' : ''}>
                  {formatMoney(inv.balance_due)}
                </td>
                <td>
                  <button className="link-btn" onClick={() => fetchInvoiceDetail(inv.id)}>
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default MyInvoices;
